/** AgentSelector — full (home screen) and toolbar (pill dropdown) variants. */

import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Search } from "lucide-react";
import cogsolAvatar from "../assets/cogsol-logo.svg";

const AgentSelector = ({
  retrievals,
  selectedRetrieval,
  currentDescription,
  nameColor,
  agentColor,
  onChange,
  disabled,
  variant = "inline",
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearch("");
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isOpen) {
        setIsOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const filtered = search
    ? retrievals.filter((r) =>
        r.description.toLowerCase().includes(search.toLowerCase()),
      )
    : retrievals;

  const handleSelect = (id) => {
    onChange(id);
    setIsOpen(false);
    setSearch("");
  };

  const selectedLabel =
    currentDescription ||
    retrievals.find((r) => r.id === selectedRetrieval)?.description ||
    t("selectAssistantDropdown");

  // Full-page variant — centered combobox, opens on click
  if (variant === "full") {
    return (
      <div className="flex flex-col items-center justify-center h-full pb-20" ref={containerRef}>
        <img src={cogsolAvatar} alt="CogSol" className="w-44 h-44 mb-6" />
        <h2 className="text-lg font-medium text-gray-900 mb-1">{t("chooseAssistant")}</h2>
        <p className="text-sm text-gray-400 mb-6">{t("selectAssistantDropdown")}</p>
        <div className="relative w-full max-w-xs">
          <button
            type="button"
            className="w-full flex items-center justify-between px-4 py-2.5 text-sm bg-white border border-gray-200 rounded-xl hover:border-gray-300 transition-colors cursor-pointer text-gray-700 disabled:opacity-50"
            onClick={() => !disabled && setIsOpen(!isOpen)}
            disabled={disabled}
          >
            <span className="text-gray-400">{t("selectAssistantDropdown")}</span>
            <ChevronDown size={16} className={`text-gray-400 shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
          </button>
          {isOpen && (
            <div className="absolute z-50 mt-2 w-full bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="p-2">
                <div className="relative">
                  <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  <input
                    ref={inputRef}
                    type="text"
                    className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg outline-none placeholder:text-gray-400 focus:border-gray-400 transition-colors bg-gray-50"
                    placeholder={t("selectAssistantDropdown")}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    autoFocus
                  />
                </div>
              </div>
              <ul className="max-h-[250px] overflow-y-auto py-1" style={{ scrollbarWidth: "thin" }}>
                {filtered.length === 0 ? (
                  <li className="px-4 py-3 text-sm text-gray-400 text-center">
                    {t("noResults")}
                  </li>
                ) : (
                  filtered.map((r) => (
                    <li key={r.id}>
                      <button
                        type="button"
                        className="w-full text-left px-4 py-2 text-sm text-gray-800 hover:bg-gray-50 transition-colors cursor-pointer"
                        onClick={() => handleSelect(r.id)}
                      >
                        {r.description}
                      </button>
                    </li>
                  ))
                )}
              </ul>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Toolbar variant — pill with dot + name + chevron, dropdown opens upward
  if (variant === "toolbar") {
    return (
      <div ref={containerRef} className="relative">
        <button
          type="button"
          className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-900 transition-colors font-sans disabled:opacity-50 disabled:cursor-default cursor-pointer"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
        >
          <span
            className="w-1.5 h-1.5 rounded-full shrink-0"
            style={{ backgroundColor: agentColor || "#10b981" }}
          />
          <span className="max-w-[160px] sm:max-w-[160px] max-sm:max-w-[100px] overflow-hidden text-ellipsis whitespace-nowrap">
            {selectedLabel}
          </span>
          <ChevronDown size={12} className="shrink-0" />
        </button>
        {isOpen && (
          <DropdownList
            retrievals={filtered}
            search={search}
            onSearch={setSearch}
            onSelect={handleSelect}
            inputRef={inputRef}
            position="top"
          />
        )}
      </div>
    );
  }

  // Inline variant
  return (
    <div ref={containerRef} className="relative w-fit">
      <button
        type="button"
        className="flex items-center gap-1 px-2 py-1 text-sm hover:bg-gray-50 rounded-md disabled:opacity-50 disabled:cursor-default cursor-pointer"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
      >
        <span
          className="truncate max-w-[200px]"
          style={{ color: nameColor || "inherit" }}
        >
          {selectedLabel}
        </span>
        <ChevronDown size={14} className="text-gray-400 shrink-0" />
      </button>
      {isOpen && (
        <DropdownList
          retrievals={filtered}
          search={search}
          onSearch={setSearch}
          onSelect={handleSelect}
          inputRef={inputRef}
        />
      )}
    </div>
  );
};

const DropdownList = ({ retrievals, search, onSearch, onSelect, inputRef, position = "bottom" }) => {
  const { t } = useTranslation();

  useEffect(() => {
    inputRef.current?.focus();
  }, [inputRef]);

  const positionClasses = position === "top"
    ? "absolute bottom-full right-0 mb-1 min-w-[250px]"
    : "absolute mt-1 w-full min-w-[250px]";

  return (
    <div className={`${positionClasses} bg-white border border-gray-200 rounded-lg overflow-hidden z-50`}>
      <div className="p-2">
        <input
          ref={inputRef}
          type="text"
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md outline-none placeholder:text-gray-400 focus:border-gray-400 transition-colors"
          placeholder={t("selectAssistantDropdown")}
          value={search}
          onChange={(e) => onSearch(e.target.value)}
        />
      </div>
      <ul className="max-h-[250px] overflow-y-auto py-1 border-t border-gray-100" style={{ scrollbarWidth: "thin" }}>
        {retrievals.length === 0 ? (
          <li className="px-4 py-3 text-sm text-gray-400 text-center">
            {t("noResults")}
          </li>
        ) : (
          retrievals.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() => onSelect(r.id)}
              >
                {r.description}
              </button>
            </li>
          ))
        )}
      </ul>
    </div>
  );
};

export { AgentSelector };
